import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Button,
  Card,
  CardHeader,
  CardContent,
  Typography,
  CardActions,
  Popover
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@material-ui/icons';
import ItemCard from './ItemCard';
import DotStepper from './DotStepper';
import { makeStyles } from '@material-ui/core/styles';
import useWindowSize from '../../../customHooks/useWindowSize';

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    height: '100%',
    marginTop: 40,
    marginBottom: 20,
    [theme.breakpoints.down('xs')]: { paddingLeft: 16, paddingRight: 16 },
    [theme.breakpoints.up('xs')]: { paddingLeft: 23, paddingRight: 23 },
    [theme.breakpoints.up('md')]: { paddingLeft: 23, paddingRight: 0 },
    [theme.breakpoints.up('lg')]: { paddingLeft: 41.5, paddingRight: 18.5 },
    [theme.breakpoints.up('xl')]: { paddingLeft: 80, paddingRight: 80 }
  },
  marginRight: {
    marginRight: 32
  },
  typography: {
    marginBottom: 20
  },
  arrowBtn: {
    width: 44,
    height: 44
  },
  fontSize: {
    fontSize: 44
  }

}));

const CardSlider = (props) => {
  const { items, setSelected } = props;
  const [selectedCard, setSelectedCard] = useState(null);
  const [error, setError] = useState(null);
  const { t } = useTranslation();
  const classes = useStyles();
  const windowSize = useWindowSize();

  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const [cardsPerPage, setCardsPerPage] = useState(5);
  const [templatesToShow, setTemplatesToShow] = useState(
    items.slice(0, 5).map((template) => template)
  );
  const [lastItemIndex, setLastItemIndex] = useState(5);

  const calculateNumberOfPages = () => {
    return ~~(items.length / cardsPerPage) +
    ((items.length / cardsPerPage) % 1 > 0 ? 1 : 0);
  };

  const [numberOfPages, setNumberOfPages] = useState(
    calculateNumberOfPages(cardsPerPage)
  );

  useEffect(() => {
    setNumberOfPages(
      calculateNumberOfPages(cardsPerPage)
    );
    if (items.length) setLoading(false);
  }, [items]);

  useEffect(() => {
    if (!loading) setTemplatesToShow(items.slice(0, 5).map((template) => template));
  }, [loading, items]);

  useEffect(() => {
    function evaluateCardsPerPage () {
      if (windowSize.width < 1440 && windowSize.width >= 1024) {
        return 4;
      } else if (windowSize.width >= 1440) {
        return 5;
      } else if (windowSize.width < 1024 && windowSize.width >= 760) {
        return 3;
      } else {
        return 2;
      }
    }

    const newItemsPerPage = evaluateCardsPerPage();
    if (newItemsPerPage !== cardsPerPage) {
      const targetCard =
      selectedCard !== null
        ? activeStep * cardsPerPage + selectedCard
        : lastItemIndex - cardsPerPage;

      const pageNumber = ~~(targetCard / newItemsPerPage);

      setCardsPerPage(newItemsPerPage);
      setActiveStep(pageNumber);
      setLastItemIndex(newItemsPerPage * (pageNumber + 1));
      setTemplatesToShow(
        items
          .slice(
            pageNumber * newItemsPerPage,
            newItemsPerPage * (pageNumber + 1)
          )
          .map((flowTemplate) => flowTemplate)
      );
      if (selectedCard !== null) {
        setSelected((pageNumber * newItemsPerPage) + targetCard % newItemsPerPage);
        setSelectedCard(targetCard % newItemsPerPage);
      }
      setNumberOfPages(
        calculateNumberOfPages(newItemsPerPage)
      );
    }
  }, [windowSize, loading]);

  const handleNextPage = () => {
    setSelected(null);
    setSelectedCard(null);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setTemplatesToShow(
      items
        .slice(lastItemIndex, lastItemIndex + cardsPerPage)
        .map((item) => item)
    );
    setLastItemIndex((s) => s + cardsPerPage);
  };

  const handleBackPage = () => {
    setSelected(null);
    setSelectedCard(null);
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setTemplatesToShow(
      items
        .slice(
          lastItemIndex - 2 * cardsPerPage,
          lastItemIndex - cardsPerPage
        )
        .map((flowTemplate) => flowTemplate)
    );
    setLastItemIndex((s) => s - cardsPerPage);
  };

  return (
    <Grid container>
      {error && (
        <Popover
          open
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'center'
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'center'
          }}
        >
          <Card>
            <CardHeader title='Error' />
            <CardContent>
              <Typography>{error}</Typography>
            </CardContent>
            <CardActions>
              <Button
                variant='contained'
                onClick={() => setError(null)}
                color='primary'
              >
                {t('createFlowWithGrids:close')}
              </Button>
            </CardActions>
          </Card>
        </Popover>
      )}
      <Grid container direction='row' alignItems='center'>
        <Grid container item xs={1} direction='row' justify='flex-end'>
          <Button
            className={classes.arrowBtn}
            onClick={handleBackPage}
            disabled={activeStep === 0}
            style={{
              display: activeStep === 0 && 'none'
            }}
          >
            <KeyboardArrowLeft className={classes.fontSize} />
          </Button>
        </Grid>
        <Grid container item xs={10} spacing={3} direction='row' wrap='nowrap' justify='flex-start'>
          {templatesToShow.map((template, index) => {
            return (
              <ItemCard
                key={index}
                title={template.name || template.templateName}
                selected={selectedCard === index}
                onSelect={() => {
                  setSelectedCard(index);
                  setSelected((activeStep * cardsPerPage) + index);
                }}
              />
            );
          })}
        </Grid>
        <Grid container item xs={1} direction='row' justify='flex-start'>
          <Button
            className={classes.arrowBtn}
            onClick={handleNextPage}
            disabled={activeStep === numberOfPages - 1}
            style={{
              display: ((activeStep === numberOfPages - 1) || (numberOfPages === 0)) && 'none'
            }}
          >
            <KeyboardArrowRight className={classes.fontSize} />
          </Button>
        </Grid>
        <Grid item xs={12} style={{ marginTop: 24 }}>
          <DotStepper activeStep={activeStep} numOfSteps={numberOfPages === 0 ? 1 : numberOfPages} />
        </Grid>
      </Grid>
    </Grid>
  );
};

CardSlider.propTypes = {
  items: PropTypes.array.isRequired,
  selected: PropTypes.number,
  setSelected: PropTypes.func.isRequired
};

export default CardSlider;
